import { fmt } from "@tasduvar/engine";
import { t } from "../../i18n";

/** USCS (TS 1500) zemin grubu kodu (çift sembollüler dahil) */
export type SoilCode = "GW" | "GP" | "GM" | "GC" | "SW" | "SP" | "SM" | "SM-SC" | "SC" | "ML" | "ML-CL" | "CL" | "OL" | "MH" | "CH" | "OH" | "Pt";
/** İstinat duvarı geri dolgusu olarak uygunluk */
export type BackfillFit = "iyi" | "orta" | "uygun değil";
export interface SoilType {
  code: SoilCode;
  /** Türkçe kısa ad (çeviri anahtarı; ekranda t() ile) */
  name: string;
  /** Doğal (temel zemini) birim hacim ağırlığı γ (kN/m³) */
  gamma: number;
  /** Doğal (temel zemini) efektif içsel sürtünme açısı φ' (°) */
  phi: number;
  /** Doğal (temel zemini) efektif kohezyon c' (kN/m²) */
  c: number;
  /** Sıkıştırılmış dolgu γ (kN/m³) */
  gammaFill: number;
  /** Sıkıştırılmış dolgu φ' (°) */
  phiFill: number;
  /** Sıkıştırılmış dolgu c' (kN/m²) — dolguda güvenli tarafta hep 0 */
  cFill: number;
  /** Tipik φ' aralığı [alt, üst] (°) */
  phiRange: [number, number];
  /** Tipik c' aralığı [alt, üst] (kN/m²) */
  cRange: [number, number];
  /** Kum/çakıl (granüler) mü */
  granular: boolean;
  backfill: BackfillFit;
  /** false: temel zemini olarak uygun değil, deney şart (organik zemin, turba) */
  foundationOk: boolean;
}

type Row = [SoilCode, string, number, number, number, number, number, [number, number], [number, number], boolean, BackfillFit];
/**
 * Zemin türüne göre TİPİK değerler — tipik değer, ön tasarım içindir; DENEY YERİNE GEÇMEZ. İki set: doğal (temel zemini)
 * γ/φ'/c' ve sıkıştırılmış dolgu γ/φ' (c' = 0). Değerler yalnız bu tabloda tutulur.
 *
 * Kaynaklar:
 * - NAVFAC DM-7.02, Tablo 1 (sıkıştırılmış zeminlerin tipik özellikleri)
 * - SN 670 010b (geotechdata.info derlemesi) — φ' aralıkları
 * - USACE EM 1110-2-2502 §3-5.b, §6-5.b — geri dolguda kil kullanılmaz
 * - KGM Teknik Şartname 2013, Kısım 205.02 — granüler dolgu
 * - TBDY 2018 §16.3.2 — kohezyonlu zeminde cu
 */
const ROWS: Row[] = [
  // code   ad                                      γ     φ'  c'  γdolgu φ'dolgu φ' aralığı  c' aralığı  granüler dolgu
  ["GW", "İyi derecelenmiş çakıl", 20, 35, 0, 21, 37, [33, 40], [0, 0], true, "iyi"],
  ["GP", "Kötü derecelenmiş çakıl", 19.5, 34, 0, 20, 36, [32, 44], [0, 0], true, "iyi"],
  ["GM", "Siltli çakıl", 20, 32, 0, 21, 33, [30, 40], [0, 5], true, "orta"],
  ["GC", "Killi çakıl", 19, 30, 0, 20, 30, [28, 35], [0, 20], true, "orta"],
  ["SW", "İyi derecelenmiş kum", 19.5, 34, 0, 20, 36, [33, 43], [0, 0], true, "iyi"],
  ["SP", "Kötü derecelenmiş kum", 18.5, 32, 0, 19, 34, [30, 39], [0, 0], true, "iyi"],
  ["SM", "Siltli kum", 19, 30, 0, 19.5, 32, [27, 35], [0, 20], true, "orta"],
  ["SM-SC", "Siltli-killi kum", 19, 29, 0, 20, 31, [28, 34], [0, 15], true, "orta"],
  ["SC", "Killi kum", 18.5, 28, 5, 19.5, 30, [28, 36], [0, 15], true, "orta"],
  ["ML", "Düşük plastisiteli silt", 18, 28, 0, 18.5, 30, [27, 35], [0, 10], false, "uygun değil"],
  ["ML-CL", "Siltli kil / killi silt", 18.5, 27, 5, 19, 29, [25, 32], [0, 20], false, "uygun değil"],
  ["CL", "Düşük plastisiteli kil", 18.5, 25, 5, 19, 26, [22, 32], [0, 20], false, "uygun değil"],
  ["OL", "Organik silt/kil (düşük plastisite)", 16, 22, 5, 17, 22, [22, 32], [0, 10], false, "uygun değil"],
  ["MH", "Yüksek plastisiteli silt", 17, 23, 5, 16.5, 24, [23, 33], [0, 20], false, "uygun değil"],
  ["CH", "Yüksek plastisiteli kil", 17.5, 19, 10, 17, 19, [17, 31], [5, 25], false, "uygun değil"],
  ["OH", "Organik kil (yüksek plastisite)", 15.5, 20, 5, 16, 20, [17, 35], [0, 10], false, "uygun değil"],
  ["Pt", "Turba", 11, 5, 0, 11, 5, [0, 10], [0, 5], false, "uygun değil"],
];
/** Temel zemini olarak uygun değil (deney şart) */
const NOT_FOUNDATION: readonly SoilCode[] = ["OL", "OH", "Pt"];

export const SOIL_TYPES: readonly SoilType[] = ROWS.map(([code, name, gamma, phi, c, gammaFill, phiFill, phiRange, cRange, granular, backfill]) => ({
  code, name, gamma, phi, c, gammaFill, phiFill, cFill: 0, phiRange, cRange, granular, backfill, foundationOk: !NOT_FOUNDATION.includes(code),
}));

/** Eşdeğer yazımlar (TS 1500 / ASTM D2487'de iki sıra da görülür) */
const ALIAS: Record<string, SoilCode> = { "CL-ML": "ML-CL", "SC-SM": "SM-SC", PT: "Pt" };

export function soilType(code: string): SoilType | undefined {
  const k = code.trim();
  const c = ALIAS[k] ?? ALIAS[k.toUpperCase()] ?? k;
  return SOIL_TYPES.find((s) => s.code === c);
}

/** φ, türün tipik aralığının alt sınırından bu kadar (°) aşağıdaysa "belirgin altında" sayılır */
const PHI_MARGIN = 5;

/**
 * Girilen φ seçilen zemin türü için belirgin düşükse uyarı metni (ör. killi zeminde UU deneyinin φ ≈ 0–5° değeri), değilse null.
 * `fromReport`: değer rapordan geldi (metin "Raporda φ = …" diye başlar).
 */
export function phiCheck(code: string, phi: number | undefined, fromReport = false): string | null {
  const s = soilType(code);
  if (!s || phi === undefined || phi >= s.phiRange[0] - PHI_MARGIN) return null;
  const [lo, hi] = s.phiRange;
  return `${fromReport ? t("Raporda") : t("Girilen")} φ = ${fmt(phi, Number.isInteger(phi) ? 0 : 1)}° — ${s.granular ? t("kum/çakıl için çok düşük") : t("bu, drenajsız (UU) deney değeri olabilir")}; ${t("seçilen zemin türü için tipik φ'")} ${lo}–${hi}°. ${s.granular ? t("Raporu kontrol edin.") : t("Efektif φ' ve c' kullanın ya da raporu kontrol edin.")}`;
}
